import { debounce, isMobileBreakpoint, getLargestHeight, checkBrowserType } from "../utility/UtilityFunctions";
import { scrollDirection } from "../utility/ScrollDirection";
import { optimisedScrollAnimation } from "../utility/OptimisedScroll";

/**
 * Used to setup site critical js such as mobile/desktop switcher, optimised scroll event configurations etc...
 *
 * This should be run before all other theme related js
 */
export default class SiteVitals {
    constructor() {
        this.elements = {
            root: document.documentElement,
            html: document.documentElement,
            body: document.body,
            header: document.querySelector('.c-page-header'),
            footer: document.querySelector('.c-page-footer'),
            primaryNav: document.querySelector('.c-nav-primary'),
            announcementBanner: document.querySelector('.c-announcement-header-banner'),
            scrollToTopButton: document.querySelector('.c-page-footer__back-to-top')
        };

        this.classes = {
            isActive: 'is-active',
            navActive: 'navigation-is-active',
        };

        this.announcementBannerHeight = 0;

        // Register Custom Scroll Event
        optimisedScrollAnimation("scroll", "optimisedScroll");

        // Check browser and apply class
        checkBrowserType();

        this.setAnnouncementBannerHeight();
        this.initIntersectionObservers();
        this.bindEventListeners();
    }

    setAnnouncementBannerHeight() {
        if (this.elements.announcementBanner) {
            const slides = this.elements.announcementBanner.querySelectorAll('.swiper-slide');
            const bannerHeight = slides.length
                ? getLargestHeight(slides)
                : this.elements.announcementBanner.querySelector('p')?.getBoundingClientRect().height;

            const bannerHeightInRem = bannerHeight / 16 + 2

            this.elements.root.style.setProperty('--announcement-banner-height', `${bannerHeightInRem}rem`);
            this.announcementBannerHeight = bannerHeight;
        } else {
            this.elements.root.style.setProperty('--announcement-banner-height', `0rem`);
        }
    }

    initIntersectionObservers() {
        // Detect when footer is in view
        if (this.elements.footer) {
            this.footerObserver = new IntersectionObserver(([entry]) => {
                this.elements.body.classList.toggle("footer-in-view", entry.isIntersecting);
            }, { threshold: 0.01 });
            this.footerObserver.observe(this.elements.footer);
        }

        // Detect autoplay videos
        this.videoObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                entry.target[entry.isIntersecting ? 'play' : 'pause']();
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('video[autoplay]').forEach(video => this.videoObserver.observe(video));
    }

    bindEventListeners() {
        window.addEventListener('optimisedScroll', () => {
            window.scrollDirection = scrollDirection();

            if (this.elements.html.classList.contains(this.classes.navActive)) {
                return this.elements.html.setAttribute('data-scroll-direction', `up`);
            }

            this.elements.html.setAttribute('data-scroll-direction', `${window?.scrollDirection?.direction}`);
            this.pageIsScrolled();
        });

        // when scrolling finishes, wait, then reset scroll direction - used to reshow headers
        let timeoutId;
        window.addEventListener('optimisedScroll', debounce(() => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                this.elements.html.setAttribute('data-scroll-direction', 'up');
            }, 700);
        }, 500));

        window.addEventListener('resize', debounce(() => {
            isMobileBreakpoint();
            this.setAnnouncementBannerHeight();

            // hotfix for broken imgix resizing not reloading srcs - hence the manual trigger
            document.dispatchEvent(new Event('revealOffcanvas')); // ajaxModalHasLoaded
        }, 250));


        // Scrolls to the next .c-row; hides button if none exists.
        document.addEventListener("DOMContentLoaded", () => {
            const scrollers = document.querySelectorAll('button.c-hero__scroller[data-module="scroll-to"]');

            scrollers.forEach(button => {
                const panelGrid = button.closest('.panel-grid');

                if (!panelGrid) {
                    button.style.display = 'none';
                    return;
                }

                // Get next sibling of .panel-grid
                let nextPanel = panelGrid.nextElementSibling;

                // Look for a .c-row inside the next panel
                const targetRow = nextPanel?.querySelector('.c-row');

                if (!targetRow) {
                    button.style.display = 'none';
                    return;
                }

                // Scroll to the top of the target .c-row
                button.addEventListener('click', () => {
                    const rowTop = targetRow.getBoundingClientRect().top + window.scrollY - this.elements.header.clientHeight;
                    window.scrollTo({
                        top: rowTop,
                        left: 0,
                        behavior: 'smooth'
                    });
                });
            });
        });

        // scroll to top button
        if (this.elements.scrollToTopButton) {
            this.elements.scrollToTopButton.addEventListener('click', (event) => {
                event.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    pageIsScrolled() {
        requestAnimationFrame(() => {
            let threshold = (window.innerWidth >= 768) ? ((this.announcementBannerHeight > 0) ? this.announcementBannerHeight : 30) : 0;

            this.elements.body.classList.toggle( "page-is-scrolled", window.pageYOffset > threshold);
        });
    }
}
